import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "@/components/ui/tooltip";
import { initials, waitingTime, PRIORITY_STYLES, fmtTime } from "@/lib/helpers";
import { UserFocus, Play, Timer, Warning, CheckCircle, UsersThree, ClockCounterClockwise } from "@phosphor-icons/react";
import { usePermissions } from "@/hooks/usePermissions";

// Format minutes as "45m" for < 60, or "1h 15m" for >= 60.
function fmtDuration(min) {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export default function QueuePage() {
  const { activeBranch, user } = useAuth();
  const { can } = usePermissions();
  // iter-67j (Area 1d) — When the caller has less than `sessions:full`
  // they're treated as a limited therapist: assignee dropdown is
  // narrowed to self, and clicking cards owned by other therapists
  // is blocked at the click handler.
  const canFullSessions = can("sessions", "full");
  const canOpenSession = (item) => {
    if (canFullSessions) return true;
    const tid = item?.assigned_therapist_id;
    if (!tid || tid === user?.id) return true;
    toast.info("You can only access sessions assigned to you.");
    return false;
  };
  const [queue, setQueue] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [assignFor, setAssignFor] = useState(null); // queue item
  const [selectedTherapist, setSelectedTherapist] = useState("");
  const [selectedTherapy, setSelectedTherapy] = useState("");
  const [loading, setLoading] = useState(true);
  // Group-size override prompt: { msg, retry } — shown only to owner/admin.
  const [groupFull, setGroupFull] = useState(null);
  // Today's activity trail for one student: { item, events, loading }.
  const [history, setHistory] = useState(null);
  const navigate = useNavigate();

  const branchId = activeBranch === "all" ? null : activeBranch;

  const load = async () => {
    setLoading(true);
    try {
      const [q, t, tt] = await Promise.all([
        api.get("/queue/students", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/queue/therapists", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/therapy-types"),
      ]);
      setQueue(q.data || []);
      setTherapists(t.data || []);
      setTherapyTypes(tt.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeBranch]); // eslint-disable-line

  useEffect(() => {
    const id = setInterval(() => setQueue((q) => [...q]), 30_000);
    return () => clearInterval(id);
  }, []);

  const openAssign = (item, mode = "auto") => {
    // mode:
    //   "auto"        → regular assign; auto-starts if therapist free.
    //   "assign_only" → user clicked "Assign Only"; ALWAYS parks in
    //                   Assigned column, no session-start regardless of
    //                   therapist availability.
    setAssignFor({ ...item, __assign_mode: mode });
    setSelectedTherapist("");
    setSelectedTherapy((item.therapies_pending || [])[0] || "");
  };

  const confirmAssignAndStart = async (overrideGroupLimit = false) => {
    if (!selectedTherapist || !selectedTherapy) { toast.error("Pick therapist and therapy"); return; }
    const assignOnly = assignFor?.__assign_mode === "assign_only";
    try {
      const { data } = await api.post("/queue/assign-student", {
        queue_id: assignFor.id,
        therapist_id: selectedTherapist,
        therapy_type_id: selectedTherapy,
        auto_start: !assignOnly,  // false = Assign-Only override
        override_group_limit: overrideGroupLimit === true,
      });
      if (data.started) {
        toast.success(data.joined_group
          ? "Student added to the running group session"
          : "Session started");
        setAssignFor(null);
        navigate(`/sessions/${data.session_id}`);
        return;
      }
      toast.info("Student parked in the Assigned column. Use Start / Start All when the therapist is ready.");
      setAssignFor(null);
      await load();
    } catch (e) {
      if (handleGroupFull(e, () => confirmAssignAndStart(true))) return;
      toast.error(formatApiError(e));
    }
  };

  // A full group blocks the action. Owner/admin get an explicit override.
  const handleGroupFull = (e, retry) => {
    const detail = e?.response?.data?.detail;
    if (detail?.code !== "group_full") return false;
    if (detail.can_override) {
      setGroupFull({ msg: detail.msg, retry });
    } else {
      toast.error(detail.msg);
    }
    return true;
  };

  const openHistory = async (item) => {
    setHistory({ item, events: [], loading: true });
    try {
      const { data } = await api.get(`/queue/${item.id}/events`);
      setHistory({ item, events: data || [], loading: false });
    } catch (e) {
      setHistory(null);
      toast.error(formatApiError(e));
    }
  };

  const startOne = async (queueId, overrideGroupLimit = false) => {
    try {
      const { data } = await api.post(`/queue/${queueId}/start`, null, {
        params: overrideGroupLimit ? { override_group_limit: true } : {},
      });
      toast.success(data.joined_group
        ? "Student added to the running group session"
        : "Session started");
      navigate(`/sessions/${data.session_id}`);
    } catch (e) {
      if (handleGroupFull(e, () => startOne(queueId, true))) return;
      toast.error(formatApiError(e));
    }
  };

  const startAllForTherapist = async (therapistId, overrideGroupLimit = false) => {
    try {
      const { data } = await api.post(`/queue/start-all/${therapistId}`, null, {
        params: overrideGroupLimit ? { override_group_limit: true } : {},
      });
      toast.success(data.joined_group
        ? `Added ${data.started} student${data.started === 1 ? "" : "s"} to the running group session`
        : `Started ${data.started} session${data.started === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      if (handleGroupFull(e, () => startAllForTherapist(therapistId, true))) return;
      toast.error(formatApiError(e));
    }
  };

  // Assigned → Waiting. No confirmation, no re-assign, no auto-start; the
  // waiting timer resumes from the wait accrued before the assignment.
  const unassign = async (queueId) => {
    try {
      await api.post(`/queue/${queueId}/unassign`);
      toast.success("Student moved back to Waiting");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    { key: "waiting", title: "Waiting", tone: "amber" },
    { key: "assigned", title: "Assigned", tone: "blue" },
    { key: "in_session", title: "In Session", tone: "teal" },
  ];

  const grouped = columns.reduce((acc, c) => {
    acc[c.key] = queue.filter(q => q.status === c.key);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-1.5">Live board</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Student Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Assign checked-in students to available therapists. Board auto-refreshes waiting time.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading queue…</div>
      ) : queue.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          No students in the queue yet. Head to <b>Attendance</b> and check in a student to start.
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {columns.map(col => {
            // Group Assigned items by therapist so we can render a "Start All"
            // button per-therapist. Waiting/InSession columns don't need this.
            const items = grouped[col.key];
            const assignedByTherapist = col.key === "assigned"
              ? items.reduce((m, it) => {
                  const tid = it.assigned_therapist_id || "unassigned";
                  (m[tid] = m[tid] || []).push(it);
                  return m;
                }, {})
              : null;
            return (
              <div key={col.key} className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="overline">{col.title}</div>
                  <Badge variant="outline" className="text-xs">{items.length}</Badge>
                </div>
                <div className="space-y-3 min-h-[100px]">
                  {assignedByTherapist ? (
                    Object.entries(assignedByTherapist).map(([tid, group]) => (
                      <div key={tid} className="space-y-2">
                        {group.length > 1 && tid !== "unassigned" && (
                          <div className="flex items-center justify-between px-1 py-1 rounded-md bg-blue-50 border border-blue-200">
                            <div className="text-xs text-blue-900">
                              <span className="font-semibold">{group[0].assigned_therapist_name || "Therapist"}</span>
                              <span className="text-blue-800/70"> · {group.length} students queued</span>
                            </div>
                            <Button
                              size="sm"
                              className="h-7 gap-1 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                              onClick={() => startAllForTherapist(tid)}
                              data-testid={`start-all-${tid}`}
                            >
                              <Play size={12} weight="fill" /> Start all
                            </Button>
                          </div>
                        )}
                        {group.map(item => (
                          <QueueCard
                            key={item.id} item={item} therapyTypes={therapyTypes}
                            onAssign={() => openAssign(item, "auto")}
                            onAssignOnly={() => openAssign(item, "assign_only")}
                            onOpenSession={() => item.session_id && canOpenSession(item) && navigate(`/sessions/${item.session_id}`)}
                            onOpenCompletedSession={(sid) => navigate(`/sessions/${sid}`)}
                            onStartOne={startOne}
                            onUnassign={unassign}
                            onOpenHistory={openHistory}
                          />
                        ))}
                      </div>
                    ))
                  ) : (
                    items.map(item => (
                      <QueueCard
                        key={item.id} item={item} therapyTypes={therapyTypes}
                        onAssign={() => openAssign(item, "auto")}
                        onAssignOnly={() => openAssign(item, "assign_only")}
                        onOpenSession={() => item.session_id && canOpenSession(item) && navigate(`/sessions/${item.session_id}`)}
                        onOpenCompletedSession={(sid) => navigate(`/sessions/${sid}`)}
                        onStartOne={startOne}
                        onUnassign={unassign}
                        onOpenHistory={openHistory}
                      />
                    ))
                  )}
                  {items.length === 0 && (
                    <div className="border border-dashed border-border rounded-md p-6 text-center text-xs text-muted-foreground">
                      Nothing here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!assignFor} onOpenChange={(o)=>!o && setAssignFor(null)}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {assignFor?.__assign_mode === "assign_only"
                ? "Assign therapist (no auto-start)"
                : "Assign therapist & start session"}
            </DialogTitle>
            {assignFor?.__assign_mode === "assign_only" && (
              <p className="text-xs text-muted-foreground pt-1">
                Student will move to the Assigned column and stay there — the session timer starts only when the therapist hits Start or Start All.
              </p>
            )}
          </DialogHeader>
          {assignFor && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-muted/40">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(assignFor.student_name)}</AvatarFallback></Avatar>
                <div className="flex-1"><div className="font-medium text-sm">{assignFor.student_name}</div><div className="text-xs text-muted-foreground">Waiting {waitingTime(assignFor.waiting_started_at || assignFor.queued_at)}</div></div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Therapy</label>
                <Select value={selectedTherapy} onValueChange={setSelectedTherapy}>
                  <SelectTrigger data-testid="assign-therapy"><SelectValue placeholder="Select therapy" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {therapyTypes.filter(t => (assignFor.therapies_pending || []).includes(t.id)).map(tt => (
                      <SelectItem key={tt.id} value={tt.id}>
                        <span className="flex items-center gap-2">
                          {tt.name} · {tt.duration_min}m
                          {tt.is_group && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-teal-300 bg-teal-50 text-teal-800 px-1.5 py-0.5 text-[10px] font-medium">
                              <UsersThree size={10} weight="duotone" />
                              Group · max {tt.max_group_size || 4}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Therapist</label>
                <Select value={selectedTherapist} onValueChange={setSelectedTherapist}>
                  <SelectTrigger data-testid="assign-therapist"><SelectValue placeholder="Select therapist" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {(canFullSessions
                      ? therapists
                      // iter-67j (Area 1d) — Limited therapist can only
                      // assign to themselves.
                      : therapists.filter(t => t.therapist_id === user?.id)
                    ).map(t => (
                      <SelectItem key={t.therapist_id} value={t.therapist_id}>
                        {t.name} · {t.status} · {t.today_sessions} today
                      </SelectItem>
                    ))}
                    {therapists.length === 0 && <div className="p-3 text-xs text-muted-foreground">No therapists checked in today.</div>}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
                <Button onClick={() => confirmAssignAndStart()} className="gap-2" data-testid="confirm-assign">
                  <Play size={14} weight="fill" />
                  {assignFor?.__assign_mode === "assign_only" ? "Assign only" : "Start Session"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Group size cap reached — owner/admin may add the student anyway. */}
      <Dialog open={!!groupFull} onOpenChange={(o) => !o && setGroupFull(null)}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Group session is full</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="group-full-msg">{groupFull?.msg}</p>
          <p className="text-xs text-muted-foreground">
            You can add this student anyway. The override is recorded on the
            student's activity trail.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupFull(null)} data-testid="group-full-cancel">Cancel</Button>
            <Button
              className="gap-1.5"
              data-testid="group-full-override"
              onClick={() => { const r = groupFull?.retry; setGroupFull(null); r?.(); }}
            >
              <UsersThree size={14} weight="duotone" /> Add anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Today's activity trail for one student. */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {history?.item?.student_name} · today's activity
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="queue-history-list">
            {history?.loading && <div className="text-xs text-muted-foreground">Loading…</div>}
            {!history?.loading && (history?.events || []).length === 0 && (
              <div className="text-xs text-muted-foreground">No activity recorded today.</div>
            )}
            {(history?.events || []).map(ev => (
              <div key={ev.id} className="flex gap-3 text-sm border-l-2 border-primary/30 pl-3 py-0.5">
                <span className="font-mono text-xs text-muted-foreground w-14 shrink-0">{fmtTime(ev.at)}</span>
                <span className="flex-1">
                  {ev.label}
                  {ev.actor_name && <span className="block text-[11px] text-muted-foreground">by {ev.actor_name}</span>}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistory(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Black or white label text, whichever reads better on the therapy colour.
function readableOn(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? "#1a1a1a" : "#fff";
}


function QueueCard({ item, therapyTypes, onAssign, onAssignOnly, onOpenSession, onOpenCompletedSession, onStartOne, onUnassign, onOpenHistory }) {
  const pendingTypes = therapyTypes.filter(t => (item.therapies_pending || []).includes(t.id));
  const completed = item.completed_therapies || [];
  return (
    <Card className="hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid={`queue-card-${item.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{initials(item.student_name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{item.student_name}</div>
            {/*
              Waiting timer displays ONLY while the student is actively
              waiting. Uses `waiting_started_at` (reset per-visit) with
              `queued_at` as a fallback for pre-fix records. During
              `assigned` / `in_session` the wait timer is intentionally
              hidden — cumulative wait can still be read from
              `total_waiting_seconds` if needed for reports.
            */}
            {item.status === "waiting" && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5" data-testid={`wait-timer-${item.id}`}>
                <Timer size={12} /> {waitingTime(item.waiting_started_at || item.queued_at, item.resumed_wait_seconds)}
              </div>
            )}
          </div>
          {item.priority !== "normal" && (
            <Badge className={`${PRIORITY_STYLES[item.priority]} text-[10px] uppercase tracking-wider gap-1`}>
              {item.priority === "urgent" && <Warning size={10} weight="fill" />}
              {item.priority}
            </Badge>
          )}
          {/* Today's activity trail for this student. */}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenHistory?.(item); }}
                  aria-label="View today's activity"
                  className="shrink-0 -mt-0.5 h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  data-testid={`history-btn-${item.id}`}
                >
                  <ClockCounterClockwise size={14} weight="duotone" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Today's activity</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* Assigned column only — send the student back to Waiting. */}
          {item.status === "assigned" && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onUnassign?.(item.id); }}
                    aria-label="Unassign student"
                    className="shrink-0 -mt-0.5 -mr-0.5 h-6 w-6 rounded-md leading-none text-sm flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    data-testid={`unassign-btn-${item.id}`}
                  >
                    <span aria-hidden="true">🔄</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>Unassign student</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {pendingTypes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pendingTypes.map(t => {
              // The therapy the student was assigned/started for is
              // highlighted (solid) so the card reads at a glance.
              const active = t.id === item.active_therapy_id;
              return (
                <Badge
                  key={t.id}
                  variant="outline"
                  className={`text-[10px] ${active ? "font-semibold ring-1 ring-offset-1" : ""}`}
                  style={active
                    ? { backgroundColor: t.color, borderColor: t.color, color: readableOn(t.color) }
                    : { borderColor: `${t.color}66`, color: t.color }}
                  data-testid={active ? `active-therapy-${item.id}` : undefined}
                >
                  {t.code}
                </Badge>
              );
            })}
          </div>
        )}

        {completed.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`completed-therapies-${item.id}`}>
            {completed.map(c => (
              <button
                key={c.session_id}
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenCompletedSession?.(c.session_id); }}
                title={`View completed ${c.code} session`}
                className="inline-flex items-center gap-1 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-800 px-1.5 py-0.5 text-[10px] font-medium hover:bg-emerald-100 transition-colors"
                data-testid={`completed-badge-${c.session_id}`}
              >
                <CheckCircle size={10} weight="fill" />
                {c.code} {fmtDuration(c.duration_min)}
              </button>
            ))}
          </div>
        )}

        {item.assigned_therapist_name && (
          <div className="text-xs text-muted-foreground">
            <UserFocus size={12} className="inline mr-1" weight="duotone" />
            {item.assigned_therapist_name}
          </div>
        )}

        {/* Group roster — who else is in the room on this running session. */}
        {item.status === "in_session" && (item.session_roster || []).length > 1 && (
          <div className="rounded-md border border-teal-200 bg-teal-50/60 px-2 py-1.5" data-testid={`session-roster-${item.id}`}>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-teal-800">
              <UsersThree size={11} weight="duotone" />
              Group session · {item.session_roster.length} students
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {item.session_roster.map(m => (
                <span
                  key={m.student_id}
                  className={`text-[11px] rounded px-1.5 py-0.5 ${m.is_self ? "bg-teal-600 text-white font-medium" : "bg-white text-teal-900 border border-teal-200"}`}
                >
                  {m.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {item.status === "waiting" && (
            <>
              <Button size="sm" className="flex-1 gap-1.5" onClick={onAssign} data-testid={`assign-btn-${item.id}`}>
                <Play size={14} weight="fill" /> Assign &amp; Start
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 gap-1.5"
                onClick={onAssignOnly}
                title="Park the student under a therapist without starting a session."
                data-testid={`assign-only-btn-${item.id}`}
              >
                Assign only
              </Button>
            </>
          )}
          {item.status === "assigned" && (
            <Button
              size="sm"
              className="w-full gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => onStartOne?.(item.id)}
              data-testid={`start-btn-${item.id}`}
            >
              <Play size={14} weight="fill" /> Start
            </Button>
          )}
          {item.status === "in_session" && (
            <Button size="sm" variant="outline" className="w-full" onClick={onOpenSession}>
              <span className="relative inline-flex mr-1.5"><span className="status-dot bg-teal-500 pulse-dot relative" /></span>
              In progress
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
